import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/contexts/LanguageContext';
import { LegalDocumentContent } from './LegalDocumentContent';

interface LegalDocumentViewDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Callback when the dialog is closed */
  onClose: () => void;
  /** The type of legal document (e.g., 'privacy-policy', 'terms-of-use') */
  documentType: string;
  /** The version of the document to display */
  version: string;
}

/**
 * Dialog for viewing a legal document (read-only).
 * Used from the settings page to view Privacy Policy or Terms of Use.
 */
export function LegalDocumentViewDialog({
  open,
  onClose,
  documentType,
  version,
}: LegalDocumentViewDialogProps) {
  const { t } = useLanguage();

  /**
   * Get document title based on type
   */
  const getDocumentTitle = () => {
    if (documentType === 'privacy-policy') {
      return t.dialogs.agreementAcceptance.privacyPolicyTitle;
    }
    if (documentType === 'terms-of-use') {
      return t.dialogs.agreementAcceptance.termsOfUseTitle;
    }
    return t.dialogs.agreementAcceptance.title;
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{getDocumentTitle()}</DialogTitle>
          <DialogDescription>
            {t.settings.legalDocuments.version}: {version}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 min-h-0">
          <LegalDocumentContent
            documentType={documentType}
            version={version}
            showScrollIndicator={false}
          />
        </div>

        <DialogFooter className="pt-4">
          <Button type="button" onClick={onClose}>
            {t.settings.legalDocuments.close}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
